import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Cookie, Settings, X, ChevronDown } from "lucide-react";
import { useNavigate } from "react-router-dom";

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [countdown, setCountdown] = useState(8);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
    functional: false,
  });
  const navigate = useNavigate();

  useEffect(() => {
    const consent = localStorage.getItem("cookieConsent");
    if (!consent) {
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    if (isVisible && countdown > 0 && !showCustomize) {
      const timer = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    } else if (countdown === 0 && !showCustomize) {
      handleAcceptAll();
    }
  }, [isVisible, countdown, showCustomize]);

  const handleAcceptAll = () => {
    const consent = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem("cookieConsent", JSON.stringify(consent));
    setIsVisible(false);
  };

  const handleRejectAll = () => {
    const consent = {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem("cookieConsent", JSON.stringify(consent));
    setIsVisible(false);
  };

  const handleSavePreferences = () => {
    const consent = {
      ...preferences,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem("cookieConsent", JSON.stringify(consent));
    setIsVisible(false);
    setShowCustomize(false);
  };

  const handleTogglePreference = (key: keyof typeof preferences) => {
    if (key === "necessary") return;
    setPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleClose = () => {
    setIsVisible(false);
  };

  if (!isVisible) return null;

  const cookieCategories = [
    {
      key: "necessary" as const,
      title: "Essential",
      description: "Required for site functionality",
      required: true,
    },
    {
      key: "functional" as const,
      title: "Functional",
      description: "Enhanced features & personalization",
      required: false,
    },
    {
      key: "analytics" as const,
      title: "Analytics",
      description: "Usage statistics & improvements",
      required: false,
    },
    {
      key: "marketing" as const,
      title: "Marketing",
      description: "Targeted advertising",
      required: false,
    },
  ];

  return (
    <AnimatePresence mode="wait">
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{
          y: 100,
          opacity: 0,
          transition: {
            duration: 0.35,
            ease: [0.25, 0.1, 0.25, 1],
          },
        }}
        transition={{
          type: "spring",
          damping: 30,
          stiffness: 200,
          mass: 0.8,
        }}
        className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
      >
        <div className="max-w-4xl mx-auto">
          <Card className="bg-accent-panel/98 backdrop-blur-md border-gray-700 shadow-2xl overflow-hidden">
            <CardContent className="p-0">
              <AnimatePresence mode="wait">
                {!showCustomize ? (
                  <motion.div
                    key="main"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{
                      duration: 0.3,
                      ease: [0.25, 0.1, 0.25, 1],
                    }}
                    className="p-5"
                  >
                    <div className="flex items-start gap-4">
                      {/* Icon & Content */}
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <motion.div
                          animate={{
                            rotate: [0, -10, 10, -10, 0],
                          }}
                          transition={{
                            duration: 2.5,
                            repeat: Infinity,
                            repeatDelay: 4,
                            ease: [0.25, 0.1, 0.25, 1],
                          }}
                        >
                          <Cookie className="w-7 h-7 text-electric-blue flex-shrink-0 mt-0.5" />
                        </motion.div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-semibold text-text-primary text-base">Cookie Preferences</h3>
                            {countdown > 0 && (
                              <motion.div
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ duration: 0.3, ease: [0.34, 1.56, 0.64, 1] }}
                                className="flex items-center gap-1.5 text-xs bg-electric-blue/15 text-electric-blue px-2.5 py-0.5 rounded-full border border-electric-blue/30"
                              >
                                <motion.span
                                  key={countdown}
                                  initial={{ scale: 1.2, opacity: 0 }}
                                  animate={{ scale: 1, opacity: 1 }}
                                  transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
                                  className="font-mono font-semibold"
                                >
                                  {countdown}s
                                </motion.span>
                              </motion.div>
                            )}
                          </div>

                          <p className="text-gray-300 text-sm mb-2">
                            We use cookies to enhance your experience.{" "}
                            <button
                              onClick={() => setShowDetails(!showDetails)}
                              className="text-electric-blue hover:underline inline-flex items-center gap-0.5 transition-colors duration-200"
                            >
                              {showDetails ? "Less" : "More"} info
                              <motion.div
                                animate={{ rotate: showDetails ? 180 : 0 }}
                                transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
                              >
                                <ChevronDown className="w-3 h-3" />
                              </motion.div>
                            </button>
                          </p>

                          <AnimatePresence>
                            {showDetails && (
                              <motion.p
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
                                className="text-gray-400 text-xs overflow-hidden"
                              >
                                We analyze usage and personalize content. See our{" "}
                                <button
                                  onClick={() => navigate("/privacy-policy")}
                                  className="text-electric-blue hover:underline transition-colors duration-200"
                                >
                                  Privacy Policy
                                </button>{" "}
                                for details.
                              </motion.p>
                            )}
                          </AnimatePresence>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-start gap-2">
                        <motion.div
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          transition={{ duration: 0.15, ease: [0.25, 0.1, 0.25, 1] }}
                        >
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowCustomize(true)}
                            aria-label="Customize cookie preferences"
                            className="text-gray-400 hover:text-text-primary hover:bg-gray-700/50 h-8 px-2 transition-all duration-200"
                          >
                            <Settings className="w-4 h-4" />
                          </Button>
                        </motion.div>

                        <motion.div
                          whileHover={{ scale: 1.1, rotate: 90 }}
                          whileTap={{ scale: 0.9 }}
                          animate={
                            countdown <= 2
                              ? {
                                  scale: [1, 1.15, 1],
                                  rotate: [0, -10, 10, -10, 0],
                                }
                              : {}
                          }
                          transition={{
                            duration: countdown <= 2 ? 0.5 : 0.2,
                            repeat: countdown <= 2 ? Infinity : 0,
                            repeatDelay: countdown <= 2 ? 0.3 : 0,
                            ease: [0.25, 0.1, 0.25, 1],
                          }}
                        >
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleClose}
                            aria-label="Close cookie consent banner"
                            className="text-gray-400 hover:text-red-400 hover:bg-red-400/10 h-8 w-8 p-0 transition-all duration-200"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </motion.div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1, duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
                      className="flex gap-2 mt-4 ml-10"
                    >
                      <motion.div
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        transition={{ duration: 0.15, ease: [0.25, 0.1, 0.25, 1] }}
                      >
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleRejectAll}
                          className="border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white text-sm h-9 transition-all duration-200"
                        >
                          Reject
                        </Button>
                      </motion.div>

                      <motion.div
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="flex-1"
                        transition={{ duration: 0.15, ease: [0.25, 0.1, 0.25, 1] }}
                      >
                        <Button
                          onClick={handleAcceptAll}
                          className="bg-electric-blue hover:bg-electric-blue/90 text-white text-sm h-9 w-full shadow-lg shadow-electric-blue/20 transition-all duration-200 font-semibold"
                        >
                          Accept All
                        </Button>
                      </motion.div>
                    </motion.div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="customize"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
                    className="p-5"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-text-primary flex items-center gap-2">
                        <Settings className="w-5 h-5 text-electric-blue" />
                        Cookie Settings
                      </h3>
                      <motion.div
                        whileHover={{ scale: 1.1, rotate: 90 }}
                        whileTap={{ scale: 0.9 }}
                        transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
                      >
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowCustomize(false)}
                          aria-label="Close cookie settings"
                          className="text-gray-400 hover:text-text-primary h-8 w-8 p-0 transition-all duration-200"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </motion.div>
                    </div>

                    <div className="space-y-2 mb-4 max-h-64 overflow-y-auto">
                      {cookieCategories.map((cookie, index) => (
                        <motion.div
                          key={cookie.key}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{
                            delay: index * 0.05,
                            duration: 0.3,
                            ease: [0.25, 0.1, 0.25, 1],
                          }}
                          className="flex items-center justify-between p-3 bg-dark-bg/50 rounded-lg hover:bg-dark-bg/70 transition-colors duration-200"
                        >
                          <div className="flex-1 pr-4">
                            <h4 className="font-medium text-text-primary text-sm mb-0.5">{cookie.title}</h4>
                            <p className="text-xs text-gray-400">{cookie.description}</p>
                          </div>

                          {cookie.required ? (
                            <span className="text-xs text-gray-500 bg-gray-700/70 px-2.5 py-1 rounded-md font-medium">
                              Always On
                            </span>
                          ) : (
                            <motion.button
                              onClick={() => handleTogglePreference(cookie.key)}
                              aria-label={`Toggle ${cookie.title} cookies`}
                              className={`relative w-11 h-6 rounded-full transition-colors duration-200 ${
                                preferences[cookie.key] ? "bg-electric-blue" : "bg-gray-600"
                              }`}
                              whileTap={{ scale: 0.95 }}
                            >
                              <motion.div
                                className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-md"
                                animate={{
                                  x: preferences[cookie.key] ? 20 : 0,
                                }}
                                transition={{
                                  type: "spring",
                                  stiffness: 600,
                                  damping: 35,
                                  mass: 0.8,
                                }}
                              />
                            </motion.button>
                          )}
                        </motion.div>
                      ))}
                    </div>

                    <div className="flex gap-2">
                      <motion.div
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="flex-1"
                        transition={{ duration: 0.15, ease: [0.25, 0.1, 0.25, 1] }}
                      >
                        <Button
                          variant="outline"
                          onClick={handleRejectAll}
                          className="w-full border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white h-9 transition-all duration-200"
                        >
                          Reject All
                        </Button>
                      </motion.div>
                      <motion.div
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="flex-1"
                        transition={{ duration: 0.15, ease: [0.25, 0.1, 0.25, 1] }}
                      >
                        <Button
                          onClick={handleSavePreferences}
                          className="w-full bg-electric-blue hover:bg-electric-blue/90 text-white h-9 shadow-lg shadow-electric-blue/20 transition-all duration-200"
                        >
                          Save Preferences
                        </Button>
                      </motion.div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </CardContent>
          </Card>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CookieConsent;
